import React, { useEffect, useState } from "react";
import { useCloseOnBack } from "../../components/common/useCloseOnBack";
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiX, FiHash } from 'react-icons/fi';
import { FaFortAwesome, FaTicketAlt, FaGolfBall } from 'react-icons/fa';
import { isRoundBased } from './roundTicket';
import './TicketDetailsModal.css';

function getTicketIcon(ticket) {
  const name = (ticket?.name || '').toLowerCase();
  if (name.includes('golf')) return { icon: <FaGolfBall size={22} />, color: 'green' };
  if (ticket?.category === 'klippekort') return { icon: <FaTicketAlt size={22} />, color: 'purple' };
  return { icon: <FaFortAwesome size={22} />, color: 'orange' };
}

export default function TicketDetailsModal({ ticket, tickets, onClose }) {
  const { t } = useTranslation('ticketDetailsModal');
  const [visible, setVisible] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0); // which ticket within a bulk group is shown

  useEffect(() => {
    const timeoutId = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    // Lock body scroll and prevent overscroll (iOS and Android)
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'contain';
    document.body.style.webkitOverflowScrolling = 'touch';
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.touchAction = '';
      document.body.style.overscrollBehavior = '';
      document.body.style.webkitOverflowScrolling = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose();
    }, 280);
  };
  useCloseOnBack(handleClose);

  const handleDragStart = (clientY) => {
    setIsDragging(true);
    setStartY(clientY);
  };

  const handleDragMove = (clientY) => {
    if (!isDragging) return;
    const deltaY = clientY - startY;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    if (dragY > 140) {
      handleClose();
    } else {
      setDragY(0);
    }
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    handleDragMove(e.touches[0].clientY);
  };

  const handleMouseDown = (e) => {
    handleDragStart(e.clientY);
  };

  useEffect(() => {
    if (!isDragging) return;

    const onMouseMove = (e) => {
      const deltaY = e.clientY - startY;
      if (deltaY > 0) {
        setDragY(deltaY);
      }
    };

    const onMouseUp = () => {
      setIsDragging(false);
      if (dragY > 140) {
        setVisible(false);
        setTimeout(() => {
          onClose();
        }, 280);
      } else {
        setDragY(0);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isDragging, startY, dragY, onClose]);

  useEffect(() => {
    // Aggressively prevent pull-to-refresh on iOS/Safari
    const preventPullToRefresh = (e) => {
      // Prevent pull-to-refresh when at the top of the page/modal
      const target = e.target;
      const isScrollable = target.scrollHeight > target.clientHeight;
      const isAtTop = target.scrollTop === 0;
      
      if (window.scrollY <= 1 || (isScrollable && isAtTop)) {
        // If swiping down at the top, prevent it
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    };

    // Add listener with passive: false to ensure preventDefault works on iOS
    document.addEventListener('touchmove', preventPullToRefresh, { passive: false });
    
    return () => {
      document.removeEventListener('touchmove', preventPullToRefresh);
    };
  }, []);

  if (!ticket) return null;

  // Viewing a bulk-activated stack: page through each ticket's own real details
  // (ref, order, dates, etc. are never the same across tickets) instead of only ever
  // showing the one representative ticket the modal was opened with.
  const groupTickets = Array.isArray(tickets) && tickets.length > 0 ? tickets : null;
  const clampedIndex = groupTickets ? Math.min(activeIndex, groupTickets.length - 1) : 0;
  const displayTicket = groupTickets ? groupTickets[clampedIndex] : ticket;

  const formatPurchaseDateTime = (value) => {
    if (!value) return t('unknown');
    try {
      let d = null;
      if (value instanceof Date) {
        d = value;
      } else if (typeof value === "string") {
        const parsed = new Date(value);
        if (!isNaN(parsed)) d = parsed;
      } else if (typeof value === "number") {
        d = new Date(value > 1e12 ? value : value * 1000);
      } else if (typeof value === "object") {
        if ("seconds" in value) d = new Date(value.seconds * 1000);
        else if ("_seconds" in value) d = new Date(value._seconds * 1000);
      }
      if (!d) return t('unknown');
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      const hour = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year} kl. ${hour}:${min}`;
    } catch {
      return t('unknown');
    }
  };

  const toNumber = (v) => {
    if (typeof v === 'number') return v;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
  };

  // Unlike toNumber, doesn't fall back to 0 for a missing field - that fallback is right
  // for math (durations) but wrong for "does this ticket even have a quantity/price"
  // checks, since a redeemed klippekort ticket has neither and shouldn't show "0".
  const toNumberOrNull = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const n = typeof v === 'number' ? v : parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };

  const formatHHMM = (d) => {
    const hour = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${hour}:${min}`;
  };

  const getEndTimeString = () => {
    try {
      if (displayTicket && displayTicket.activatedAt && Number.isFinite(displayTicket.totalDuration)) {
        let startMs = null;
        if (typeof displayTicket.activatedAt === 'number') {
          startMs = displayTicket.activatedAt;
        } else if (typeof displayTicket.activatedAt === 'object') {
          if ('seconds' in displayTicket.activatedAt) startMs = displayTicket.activatedAt.seconds * 1000;
          else if ('_seconds' in displayTicket.activatedAt) startMs = displayTicket.activatedAt._seconds * 1000;
        }
        if (startMs) {
          const end = new Date(startMs + (displayTicket.totalDuration * 1000));
          return formatHHMM(end);
        }
      }
      // No activatedAt means there's no session actually running right now - don't
      // project a fake "ends at" time from the raw duration alone (that used to show
      // a nonsense end time, e.g. "now + 24h", for a day pass that isn't activated).
      return null;
    } catch {
      return null;
    }
  };

  const bonusMin = toNumber(displayTicket?.bonusMinutes) || toNumber(displayTicket?.bonusTime);
  // duration is the base play time; the bonus comes on top (as when the ticket is started)
  const baseMin = toNumber(displayTicket?.duration);
  const isDayPass = (displayTicket?.name || '').toLowerCase().includes('lek hele dagen');
  const isRound = isRoundBased(displayTicket);
  const displayQuantity = toNumberOrNull(displayTicket?.quantity);
  const displayPrice = toNumberOrNull(displayTicket?.price);
  const endStr = getEndTimeString();
  const activatedSource = displayTicket?.activatedAt || displayTicket?.printedAt || null;
  const activatedStr = activatedSource
    ? formatPurchaseDateTime(activatedSource)
    : t('notActivated');
  const activatedLabel = t('activated');

  // BR = "billettreferanse", matches the OR/BR short-id convention used
  // across the app (PaymentReturn.jsx, pws-con's order lists) - the full
  // UUID is still the real id used for lookups, this is display-only.
  const formatTicketRef = (ref) => {
    if (!ref || ref === t('unknown')) return t('unknown');
    return `BR-${ref.substring(0, 8)}`;
  };

  const uniqueTicketRef = displayTicket?.ticketRef || displayTicket?.firestoreTicketDocId || displayTicket?.id || t('unknown');
  const displayTicketRef = formatTicketRef(uniqueTicketRef);

  const { icon, color } = getTicketIcon(displayTicket);

  return ReactDOM.createPortal(
    <div
      className={`ticket-details-overlay ${visible ? 'visible' : 'hidden'}`}
      onClick={handleClose}
      style={{ opacity: visible ? Math.max(0, 1 - dragY / 320) : 0 }}
    >
      <div
        className={`ticket-details-sheet ${visible ? 'visible' : 'hidden'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            transform: `translateY(${dragY}px)`,
            transition: isDragging ? 'none' : 'transform 0.28s ease'
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="ticket-details-handle" />
          <div className="ticket-details-header">
            <div className="ticket-details-header-left">
              <div className={`ticket-details-icon ticket-details-icon--${color}`}>{icon}</div>
              <div>
                <div className="ticket-details-title">{displayTicket.name}</div>
                <div className="ticket-details-subtitle">{t('ticketDetails')}</div>
              </div>
            </div>
            <button className="ticket-details-close" onClick={handleClose} aria-label={t('close')}>
              <FiX size={16} />
            </button>
          </div>
          {groupTickets && groupTickets.length > 1 && (
            <div className="ticket-details-pager">
              <button
                className="ticket-details-pager-btn"
                disabled={clampedIndex === 0}
                onClick={() => setActiveIndex(i => Math.max(0, i - 1))}
                aria-label={t('previousTicket')}
              >
                ‹
              </button>
              <span className="ticket-details-pager-label">{t('ticketXOfY', { current: clampedIndex + 1, total: groupTickets.length })}</span>
              <button
                className="ticket-details-pager-btn"
                disabled={clampedIndex === groupTickets.length - 1}
                onClick={() => setActiveIndex(i => Math.min(groupTickets.length - 1, i + 1))}
                aria-label={t('nextTicket')}
              >
                ›
              </button>
            </div>
          )}
          {(displayTicket?.firestoreKlippekortDocId || displayTicket?.sourceKlippekortId || displayTicket?.fromKlippekort) && (
            <div className="ticket-details-klippekort-section">
              <span className="ticket-details-klippekort-badge">
                <FiHash size={12} />
                {displayTicket.klippekortId ? `KK-${displayTicket.klippekortId.substring(0, 8)}` : (displayTicket.firestoreKlippekortDocId || displayTicket.sourceKlippekortId || t('fromKlippekort'))}
              </span>
            </div>
          )}
          <div className="ticket-details-body">
            <div className="ticket-details-meta">
              <div className="ticket-details-meta-row">
                <span>{t('customer')}</span>
                <strong>{displayTicket.customerName || t('unknown')}</strong>
              </div>
              <div className="ticket-details-meta-row">
                <span>{t('orderId')}</span>
                <strong>{displayTicket.orderReference ? `OR-${displayTicket.orderReference.substring(0, 8)}` : t('unknown')}</strong>
              </div>
              <div className="ticket-details-meta-row">
                <span>{t('ticketRef')}</span>
                <strong>{displayTicketRef}</strong>
              </div>
              <div className="ticket-details-meta-row">
                <span>{t('purchaseDate')}</span>
                <strong>{formatPurchaseDateTime(displayTicket.datePurchased || displayTicket.purchaseDate || displayTicket.orderDate || displayTicket.createdAt || displayTicket.created || displayTicket.timestamp)}</strong>
              </div>
              <div className="ticket-details-meta-row">
                <span>{t('category')}</span>
                <strong>{displayTicket.category || t('unknown')}</strong>
              </div>
            </div>
            <div className="ticket-details-meta">
              <div className="ticket-details-meta-row">
                <span>{t('duration')}</span>
                <strong>{isDayPass ? t('allDay') : isRound ? t('oneRound') : (baseMin ? (bonusMin > 0 ? t('durationWithBonus', { base: baseMin, bonus: bonusMin }) : t('minutes', { count: baseMin })) : t('unknown'))}</strong>
              </div>
              <div className="ticket-details-meta-row">
                <span>{activatedLabel}</span>
                <strong>{activatedStr}</strong>
              </div>
              {endStr && (
                <div className="ticket-details-meta-row">
                  <span>{t('endsAt')}</span>
                  <strong>{endStr}</strong>
                </div>
              )}
            </div>
            <div className="ticket-details-meta">
              {displayQuantity != null && (
                <div className="ticket-details-meta-row">
                  <span>{t('quantity')}</span>
                  <strong>{displayQuantity}</strong>
                </div>
              )}
              {displayPrice != null && (
                <div className="ticket-details-meta-row">
                  <span>{t('price')}</span>
                  <strong>{displayPrice} kr</strong>
                </div>
              )}
              {typeof displayTicket?.usedStamps === 'number' && (
                <div className="ticket-details-meta-row">
                  <span>{t('punchesUsed')}</span>
                  <strong>{displayTicket.usedStamps}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
